import { Component, ComponentArgs } from "@kluntje/core";
import { html, TemplateResult } from "lit-html";
import { ERROR } from "Helper/cssClasses";

export class FraFormField extends Component {
  constructor({ ui = {}, events = [], initialStates = {}, reactions = {} }: ComponentArgs) {
    super({
      ui: {
        input: ".fra-js-form-field__input :-one",
      },
      initialStates: {
        showError: false,
      },
      reactions: {
        showError: ["onErrorChange"],
      },
    });

    Object.assign(this.uiDefinitions, ui);
    Object.assign(this.initialStates, initialStates);
    Object.assign(this.reactions, reactions);

    this.mergeEvents(events);
  }

  /**
   * Returns default error message
   * @returns {string}
   */
  get DEFAULT_ERROR_MESSAGE(): string {
    return "Bitte füllen Sie dieses Feld aus.";
  }

  /**
   * Returns default field type
   * @returns {string}
   */
  get DEFAULT_FIELD_TYPE(): string {
    return "text";
  }

  /**
   * Returns default input placeholder
   * @returns {string}
   */
  get DEFAULT_PLACEHOLDER(): string {
    return "";
  }

  /**
   * Returns whether formfield is required
   * @returns {boolean}
   */
  get isRequired(): boolean {
    return this.getAttribute("required") !== null;
  }

  /**
   * Returns autocomplete type of input field
   * @returns {string}
   */
  get autocompleteType(): string {
    return this.getAttribute("autocomplete") || "";
  }

  /**
   * Returns name of input field
   * @returns {string}
   */
  get name(): string {
    return this.getAttribute("name") || "";
  }

  /**
   * Returns placeholder of input field
   * @returns {string}
   */
  get placeholder(): string {
    return this.getAttribute("placeholder") || this.DEFAULT_PLACEHOLDER;
  }

  /**
   * Returns error-message
   * @returns {string}
   */
  get errorMessage(): string {
    return this.getAttribute("error-message") || this.DEFAULT_ERROR_MESSAGE;
  }

  /**
   * Returns type of input field
   * @returns {string}
   */
  get type(): string {
    return this.getAttribute("type") || this.DEFAULT_FIELD_TYPE;
  }

  /**
   * Returns wheter input is currently valid
   * @returns {boolean}
   */
  get isValid(): boolean {
    return this.ui.input.checkValidity();
  }

  /**
   * Returns the submitvalue of the form-field
   * @returns {string}
   */
  get submitValue(): string {
    return this.ui.input.value;
  }

  /**
   * Returns the submit-data object of this formfield
   * @returns {object | false}
   */
  get submitData(): object | false {
    if (this.name === "" || this.submitValue === undefined || this.submitValue === "") return false;

    return {
      name: this.name,
      value: this.submitValue,
    };
  }

  /**
   * Validates this formfield by updating show error state
   */
  validate(): void {
    this.setState({ showError: !this.isValid });
  }

  /**
   * Handles showError-state changes by rerendering the component
   */
  onErrorChange(): void {
    this.renderComponent();
  }
}

/* ========================================================
              Template Helpers
     =======================================================*/

/**
 * Returns space-sperated list of classes
 * @param {Boolean} showError
 * @param {String} additionalClasses
 * @param {Boolean} applyFormFieldStyled
 * @returns {string}
 */
const getInputClasses = (
  showError: boolean,
  additionalClasses: string[] = [],
  applyFormFieldStyled: boolean,
): string => {
  let classList = ["fra-js-form-field__input"];

  if (applyFormFieldStyled) {
    classList.push("fra-e-form-field__input");
  }

  classList = classList.concat(additionalClasses);
  if (showError) classList.push(ERROR);

  return classList.join(" ");
};

/**
 * Returns the input placeholder
 * @param {String} placeholder
 * @param {Boolean} isRequired
 * @returns {string}
 */
const getPlaceholder = (placeholder: string, isRequired: boolean): string => {
  return `${placeholder}${isRequired ? " *" : ""}`;
};

const errorBox = (errorMessage: string) => {
  return html`
    <span class="fra-e-form-field__error">${errorMessage}</span>
  `;
};

export const getError = (showError: boolean, errorMessage: string) => {
  return showError ? errorBox(errorMessage) : html``;
};

type getInputArgs = {
  isRequired: boolean;
  autocompleteType: string;
  name: string;
  placeholder: string;
  label: string;
  type: string;
  showError: boolean;
  errorMessage: string;
  additionalClasses?: string[];
  readonly?: boolean;
  isChecked?: boolean;
  applyFormFieldStyled?: boolean;
};

/**
 * Return TemplateResult of formfield input
 * @param {getInputArgs} args
 * @returns {TemplateResult}
 */
export const getInput = (args: getInputArgs): TemplateResult => {
  return html`
    <input
      type="${args.type}"
      placeholder="${getPlaceholder(args.placeholder, args.isRequired)}"
      class="${getInputClasses(args.showError, args.additionalClasses, Boolean(args.applyFormFieldStyled))}"
      name="${args.name}"
      aria-label="${args.label}"
      ?required=${args.isRequired}
      ?readonly=${args.readonly}
      ?checked=${args.isChecked}
      autocomplete="${args.autocompleteType}"
    />
  `;
};
